import { Document, Font, Image, Page, StyleSheet, Text, View } from '@react-pdf/renderer';
import moment from 'moment';
import Tw_Cen_MT_Negrita from '../../../../componentes/font/Tw_Cen_MT/TCB_____.TTF';
import Tw_Cen_MT from '../../../../componentes/font/Tw_Cen_MT/TCM_____.TTF';
import NumeroALetras from '../../../../componentes/funciones/NumeroALetras';
import { NegocioConfig } from "../../../../configuracion/Empresa";
import { formatoMoneda } from "../../../../configuracion/validaciones";

Font.register({ family: 'Tw_Cen_MT', src: Tw_Cen_MT });
Font.register({ family: 'Tw_Cen_MT_Negrita', src: Tw_Cen_MT_Negrita });

const styles = StyleSheet.create({
    page: {
        fontFamily: 'Tw_Cen_MT',
        fontSize: 10,
        paddingTop: 40,
        paddingBottom: 50,
        paddingHorizontal: 60,
        backgroundColor: '#ffffff',
    },
    header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: 5,
    },
    companyInfoLeft: {
        flexGrow: 1,
        textAlign: 'left',
    },
    companyName: {
        fontFamily: 'Tw_Cen_MT_Negrita',
        fontSize: 16,
        color: '#333',
        marginBottom: 3,
    },
    logoContainer: {
        width: 100,
        height: 100,
        marginLeft: 20,
        marginTop: -10
    },
    logo: {
        width: 100,
        height: 100,
        objectFit: 'cover',
        borderRadius: 15,
        overflow: 'hidden',
    },
    title: {
        fontSize: 16,
        fontFamily: 'Tw_Cen_MT_Negrita',
        color: '#222',
        letterSpacing: 1,
        textTransform: 'uppercase',
        marginTop: 15,
    },
    infoGrid: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginBottom: 10,
        marginTop: 5,
    },
    infoColumn: { width: '33%' },
    infoLabel: { fontFamily: 'Tw_Cen_MT_Negrita', fontSize: 10, color: '#444', marginBottom: 2 },
    infoText: { fontSize: 10, color: '#666', marginBottom: 5 },

    sectionTitle: {
        fontFamily: 'Tw_Cen_MT_Negrita',
        fontSize: 11,
        backgroundColor: '#F9F9F9',
        padding: 5,
        marginTop: 10,
        marginBottom: 5,
        borderLeftWidth: 3,
        borderLeftColor: '#333',
    },
    financialRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        paddingVertical: 3,
        borderBottomWidth: 0.5,
        borderBottomColor: '#EEE',
    },
    totalsWrapper: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginTop: 15,
    },
    totalsContainer: {
        width: '45%',
        borderTopWidth: 1.5,
        borderTopColor: '#E0E0E0',
        paddingTop: 10,
    },
    grandTotalRow: {
        marginTop: 5,
        paddingTop: 8,
        borderTopWidth: 1,
        borderTopColor: '#D0D0D0',
        flexDirection: 'row',
        justifyContent: 'space-between',
    },
    grandTotalLabel: { fontFamily: 'Tw_Cen_MT_Negrita', fontSize: 13 },
    grandTotalValue: { fontFamily: 'Tw_Cen_MT_Negrita', fontSize: 13 },
    footer: {
        position: 'absolute',
        bottom: 30,
        left: 0,
        right: 0,
        textAlign: 'center',
        fontSize: 8,
        color: '#888',
        paddingHorizontal: 60,
    },
});

const ReporteCierre = ({ data, usuario }) => {
    if (!data) return null;

    const cierre = data.nuevoCierre || data;
    const {
        createdAt, efectivo = 0, pos = 0, transferencias = 0, id,
        efectivosistema = 0, possistema = 0, transferenciassistema = 0,
        totalventa = 0, totalcompra = 0, egreso = 0, ingreso = 0
    } = cierre;

    const codigoCierre = id ? `CE-${String(id).padStart(5, "0")}` : "—";
    const totalCaja = parseFloat(efectivo) + parseFloat(pos) + parseFloat(transferencias);
    const empleadoNombre = usuario?.datoPersonales?.nombreCorto || "—";

    const Row = (label, value) => (
        <View style={styles.financialRow}>
            <Text style={{ color: '#555' }}>{label}</Text>
            <Text style={{ fontFamily: 'Tw_Cen_MT_Negrita' }}>{formatoMoneda(value)}</Text>
        </View>
    );

    return (
        <Document>
            <Page size="LETTER" style={styles.page}>
                <View style={styles.header}>
                    <View style={styles.companyInfoLeft}>
                        <Text style={styles.companyName}>{NegocioConfig.nombre}</Text>
                        <Text style={{ fontSize: 9, color: '#666' }}>{NegocioConfig.direccion}</Text>
                        <Text style={styles.title}>CIERRE DE CAJA N° {codigoCierre}</Text>
                    </View>
                    {NegocioConfig.logo && (
                        <View style={styles.logoContainer}><Image style={styles.logo} src={NegocioConfig.logo} /></View>
                    )}
                </View>

                <View style={styles.infoGrid}>
                    <View style={styles.infoColumn}>
                        <Text style={styles.infoLabel}>Fecha de Cierre:</Text>
                        <Text style={styles.infoText}>{moment(createdAt).format('DD/MM/YYYY HH:mm')}</Text>
                    </View>
                    <View style={styles.infoColumn}>
                        <Text style={styles.infoLabel}>Responsable:</Text>
                        <Text style={styles.infoText}>{empleadoNombre}</Text>
                    </View>
                    <View style={styles.infoColumn}>
                        <Text style={styles.infoLabel}>ID Usuario:</Text>
                        <Text style={styles.infoText}>{usuario?.datoPersonales?.id || '—'}</Text>
                    </View>
                </View>

                <Text style={styles.sectionTitle}>RESUMEN DE MOVIMIENTOS</Text>
                {Row('Total Ventas (Sistema):', totalventa)}
                {Row('Total Compras (Sistema):', totalcompra)}
                {Row('Otros Ingresos:', ingreso)}
                {Row('Otros Egresos:', egreso)}

                <Text style={styles.sectionTitle}>TOTALES DEL SISTEMA</Text>
                {Row('Efectivo (Sistema):', efectivosistema)}
                {Row('POS/Tarjeta (Sistema):', possistema)}
                {Row('Transferencias (Sistema):', transferenciassistema)}

                <Text style={styles.sectionTitle}>TOTALES CONTADOS (REAL)</Text>
                {Row('Efectivo (Contado):', efectivo)}
                {Row('POS/Tarjeta (Contado):', pos)}
                {Row('Transferencias (Contado):', transferencias)}

                <View style={styles.totalsWrapper}>
                    <View style={{ width: '50%', paddingTop: 10 }}>
                        <Text style={styles.infoLabel}>VALOR TOTAL EN LETRAS:</Text>
                        <Text style={{ fontSize: 9, fontFamily: 'Tw_Cen_MT_Negrita', textTransform: 'uppercase' }}>
                            {NumeroALetras(totalCaja)}
                        </Text>
                    </View>
                    <View style={styles.totalsContainer}>
                        <View style={styles.grandTotalRow}>
                            <Text style={styles.grandTotalLabel}>TOTAL CONTADO:</Text>
                            <Text style={styles.grandTotalValue}>{formatoMoneda(totalCaja)}</Text>
                        </View>
                    </View>
                </View>

                <View style={styles.footer} fixed>
                    <Text>Cierre de caja generado exitosamente. Firma de revisado: ________________________</Text>
                </View>
            </Page>
        </Document>
    );
};

export default ReporteCierre;